import React, { useState, useRef, useEffect } from 'react';
import './Chatbox.css';
import ChatMessage from './ChatMessage';
import PresetQuestions from './PresetQuestions';
import LoadingIndicator from './LoadingIndicator';
import ChatHistory from './ChatHistory';
import TypingIndicator from './TypingIndicator';
import SearchBar from './SearchBar';
import SettingsPanel from './SettingsPanel';
import config from '../config';

const Chatbox = () => {
    const [message, setMessage] = useState('');
    const [conversation, setConversation] = useState([]);
    // Initialize thread_id from localStorage if available
    const [thread_id, setThread_id] = useState(() => {
        try {
            const savedThreadId = localStorage.getItem('currentThreadId');
            return savedThreadId || 'new';
        } catch (error) {
            return 'new';
        }
    });
    const [isMessageInProgress, setIsMessageInProgress] = useState(false);
    const [chatEnabled, setChatEnabled] = useState(true);
    const [isThinking, setIsThinking] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [isEstablishingThread, setIsEstablishingThread] = useState(false);
    const endOfMessagesRef = useRef(null);
    const chatBodyRef = useRef(null);
    const chatHistoryRef = useRef(null);
    const backendIp = config.backendIp;

    // Helper function to safely save settings to localStorage
    const safeLocalStorageSet = (key, value) => {
        try {
            localStorage.setItem(key, value);
            return true;
        } catch (error) {
            return false;
        }
    };

    // Debug function to log conversation state changes
    const logConversationChange = (action, details = {}) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(`[Conversation Debug] ${action}:`, {
                timestamp: new Date().toISOString(),
                threadId: thread_id,
                conversationLength: conversation.length,
                ...details
            });
        }
    };

    // Function to fetch conversation from backend
    const fetchConversationFromBackend = async (threadId) => {
        if (threadId === 'new') {
            return [];
        }
        
        try {
            const response = await fetch(`${backendIp}/api/threads/${threadId}/messages`);
            if (response.ok) {
                const data = await response.json();
                return data.messages.map(msg => ({
                    msg: msg.content,
                    from: msg.is_user === 'user' ? 'user' : 'bot',
                    timestamp: msg.timestamp
                }));
            } else {
                return [];
            }
        } catch (error) {
            return [];
        }
    };

    useEffect(() => {
        // Check autoScroll setting before scrolling
        const savedSettings = localStorage.getItem('chatSettings');
        let autoScroll = true; // default value
        
        if (savedSettings) {
            try {
                const settings = JSON.parse(savedSettings);
                autoScroll = settings.autoScroll !== false; // default to true if not set
            } catch (error) {
                // Error loading settings for auto-scroll
            }
        }
        
        if (autoScroll && endOfMessagesRef.current) {
            // Use a small timeout to ensure DOM has updated
            setTimeout(() => {
                if (endOfMessagesRef.current) {
                    endOfMessagesRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                        inline: 'nearest'
                    });
                }
            }, 100);
        }
    }, [conversation]);

    // Save current thread_id to localStorage whenever it changes and maintain history list
    useEffect(() => {
        try {
            if (thread_id && thread_id !== 'new') {
                // Save current thread ID
                localStorage.setItem('currentThreadId', thread_id);
                // Maintain list of all thread IDs
                const storedIds = JSON.parse(localStorage.getItem('threadIds') || '[]');
                if (!storedIds.includes(thread_id)) {
                    storedIds.push(thread_id);
                    localStorage.setItem('threadIds', JSON.stringify(storedIds));
                }
                logConversationChange('Saved current thread ID to localStorage', { threadId: thread_id });
            } else if (thread_id === 'new') {
                localStorage.removeItem('currentThreadId');
                logConversationChange('Removed current thread ID from localStorage (new chat)');
            }
        } catch (error) {
            // Error saving current thread ID
        }
    }, [thread_id]);

    // Load conversation from backend when thread changes
    useEffect(() => {
        // Don't reload conversation if a message is currently being processed or thinking
        if (isMessageInProgress || isThinking || isEstablishingThread) {
            logConversationChange('Skipping conversation load - message in progress, thinking, or establishing thread', {
                threadId: thread_id,
                isMessageInProgress,
                isThinking,
                isEstablishingThread
            });
            return;
        }

        // Don't reload conversation if we just transitioned from 'new' to actual thread_id during message processing
        if (thread_id !== 'new' && conversation.length > 0 && !isEstablishingThread) {
            logConversationChange('Skipping conversation load - already have conversation loaded', {
                threadId: thread_id,
                conversationLength: conversation.length
            });
            return;
        }
        
        logConversationChange('Loading conversation for thread', { threadId: thread_id });
        
        if (thread_id === 'new') {
            logConversationChange('Setting empty conversation for new thread');
            setConversation([]);
            return;
        }
        
        // Fetch conversation from backend
        fetchConversationFromBackend(thread_id).then(messages => {
            if (messages.length > 0) {
                logConversationChange('Successfully loaded conversation from backend', {
                    messageCount: messages.length,
                    firstMessage: messages[0]?.msg?.substring(0, 50) + '...'
                });
                setConversation(messages);
            } else {
                logConversationChange('No conversation found in backend, resetting to new', { threadId: thread_id });
                localStorage.removeItem('currentThreadId');
                setThread_id('new');
                setConversation([]);
            }
        }).catch(error => {
            console.error('Error loading conversation from backend:', error);
            logConversationChange('Error loading conversation from backend, reset to new');
            localStorage.removeItem('currentThreadId');
            setThread_id('new');
            setConversation([]);
        });
    }, [thread_id, isMessageInProgress, isThinking, isEstablishingThread]);

    // Note: Conversation saving is now handled by the backend
    // No need to save to localStorage as backend persists all messages

    const sendMessage = async (messageToSend = message, isRegeneration = false) => {
        if (!messageToSend.trim()) return;
        
        logConversationChange('Sending message', {
            message: messageToSend.substring(0, 50) + '...',
            currentConversationLength: conversation.length,
            isRegeneration,
            currentThreadId: thread_id
        });
        
        setIsMessageInProgress(true);
        setChatEnabled(false);
        if (!isRegeneration) {
            setMessage('');
        }
        
        // Set establishing thread state if we're starting with a 'new' thread
        if (thread_id === 'new') {
            setIsEstablishingThread(true);
        }
    
        // Only add user message if this is not a regeneration
        if (!isRegeneration) {
            setConversation(prev => {
                const newConv = [...prev, { msg: messageToSend, from: 'user', timestamp: new Date().toISOString() }];
                logConversationChange('Added user message', { newLength: newConv.length });
                return newConv;
            });
        }
        
        try {
            setIsThinking(true);
            setConversation(prev => {
                const newConv = [...prev, { msg: '', from: 'bot', isThinking: true, timestamp: new Date().toISOString() }];
                logConversationChange('Added thinking placeholder', { newLength: newConv.length });
                return newConv;
            });
    
            const eventSource = new EventSource(
                `${backendIp}/api/ask?message=${encodeURIComponent(messageToSend)}&thread_id=${encodeURIComponent(thread_id)}`
            );
            
            let isFirstMessage = true;
            
            eventSource.onmessage = (event) => {
                if (event.data === '[DONE]') {
                    eventSource.close();
                    setIsThinking(false);
                    setChatEnabled(true);
                    setIsMessageInProgress(false);
                    setIsEstablishingThread(false);
                    
                    // Chat history saving is now handled by the useEffect hook
                    // No need to save here to avoid duplicates
                    logConversationChange('Message completed, history will be saved by useEffect');
                    
                    return;
                }
    
                try {
                    const data = JSON.parse(event.data);
                    
                    if (data.thread_id) {
                        logConversationChange('Received new thread_id', {
                            oldThreadId: thread_id,
                            newThreadId: data.thread_id
                        });
                        
                        // Only update thread_id if it's actually different and we're in a 'new' conversation
                        if (data.thread_id !== thread_id && thread_id === 'new') {
                            setThread_id(data.thread_id);
                            // Clear establishing thread state since we now have the actual thread_id
                            setIsEstablishingThread(false);
                        } else if (data.thread_id !== thread_id && thread_id !== 'new') {
                            // Log unexpected thread_id change but don't update to prevent UI reset
                            console.warn('Unexpected thread_id change during active conversation:', {
                                current: thread_id,
                                received: data.thread_id
                            });
                        }
                    } else if (data.content) {
                        setConversation(prev => {
                            const newConversation = [...prev];
                            if (isFirstMessage) {
                                newConversation[newConversation.length - 1] = {
                                    msg: data.content,
                                    from: 'bot',
                                    isThinking: false,
                                    timestamp: new Date().toISOString()
                                };
                                isFirstMessage = false;
                            } else {
                                const lastMessage = newConversation[newConversation.length - 1];
                                newConversation[newConversation.length - 1] = {
                                    msg: lastMessage.msg + data.content,
                                    from: 'bot',
                                    isThinking: false,
                                    timestamp: lastMessage.timestamp || new Date().toISOString()
                                };
                            }
                            return newConversation;
                        });
                    }
                } catch (error) {
                    // Error parsing event data
                }
            };
    
            eventSource.onerror = (error) => {
                console.error('EventSource failed:', error);
                eventSource.close();
                setIsThinking(false);
                setIsMessageInProgress(false);
                setIsEstablishingThread(false);
                setConversation(prev => {
                    const newConversation = [...prev];
                    const lastMessage = newConversation[newConversation.length - 1];
                    
                    // Only replace with error message if the last message is empty or still thinking
                    if (!lastMessage.msg || lastMessage.isThinking) {
                        newConversation[newConversation.length - 1] = {
                            msg: "Failed to get response. Please try again.",
                            from: 'bot',
                            isThinking: false,
                            timestamp: new Date().toISOString()
                        };
                    } else {
                        // If there's already content, just stop thinking state
                        newConversation[newConversation.length - 1] = {
                            ...lastMessage,
                            isThinking: false
                        };
                    }
                    return newConversation;
                });
                setChatEnabled(true);
            };
    
        } catch (error) {
            console.error("Error when sending message: ", error);
            setIsThinking(false);
            setIsMessageInProgress(false);
            setIsEstablishingThread(false);
            setConversation(prev => {
                const newConversation = [...prev];
                const lastMessage = newConversation[newConversation.length - 1];
                
                // Only replace with error message if the last message is empty or still thinking
                if (!lastMessage.msg || lastMessage.isThinking) {
                    newConversation[newConversation.length - 1] = {
                        msg: "Failed to get response. Please try again.",
                        from: 'bot',
                        isThinking: false,
                        timestamp: new Date().toISOString()
                    };
                } else {
                    // If there's already content, just stop thinking state
                    newConversation[newConversation.length - 1] = {
                        ...lastMessage,
                        isThinking: false
                    };
                }
                return newConversation;
            });
            setChatEnabled(true);
        }
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        sendMessage();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        } else if (event.ctrlKey && event.key === 'f') {
            event.preventDefault();
            setShowSearch(true);
        }
    };

    // Global keyboard shortcut for search
    useEffect(() => {
        const handleGlobalKeyDown = (event) => {
            if (event.ctrlKey && event.key === 'f' && !showSearch) {
                event.preventDefault();
                setShowSearch(true);
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, [showSearch]);

    const clearChat = (e) => {
        e.preventDefault();
        
        logConversationChange('Clear chat button clicked - starting new conversation', {
            currentThreadId: thread_id,
            conversationLength: conversation.length
        });
        
        // Use the same logic as "New Chat" in history panel
        handleSelectConversation('new');
    };

    const handleSelectConversation = (selectedThreadId) => {
        logConversationChange('Switching conversation', {
            from: thread_id,
            to: selectedThreadId
        });
        
        // Prevent switching if already processing
        if (isMessageInProgress || isThinking || isEstablishingThread) {
            return;
        }
        
        // No need to save conversation before switching - backend handles persistence
        
        // Reset any ongoing states but preserve settings
        setIsThinking(false);
        setIsMessageInProgress(false);
        setIsEstablishingThread(false);
        setChatEnabled(true);
        setMessage('');
        setSearchResults([]);
        
        // Close any open panels to prevent state conflicts
        setShowSearch(false);
        setShowHistory(false);
        // Note: Don't close settings panel as user might be switching conversations while configuring
        
        // Handle new conversation immediately
        if (selectedThreadId === 'new') {
            logConversationChange('Starting new conversation - clearing immediately');
            
            // Clear current thread ID from localStorage
            localStorage.removeItem('currentThreadId');
            
            // Set state immediately
            setConversation([]);
            setThread_id('new');
            return;
        }
        
        // For existing conversations, clear first then set thread_id to trigger loading
        setConversation([]);
        setThread_id(selectedThreadId);
        
        logConversationChange('Thread ID updated, conversation loading will be handled by useEffect', {
            newThreadId: selectedThreadId
        });
    };

    const handleRegenerateResponse = () => {
        // Prevent regeneration if already processing a message
        if (isMessageInProgress || isThinking) {
            return;
        }

        if (conversation.length >= 2) {
            logConversationChange('Starting regenerate response', {
                conversationLength: conversation.length
            });

            // Get the last user message
            const lastUserMessage = [...conversation].reverse().find(msg => msg.from === 'user');
            if (lastUserMessage) {
                // Remove the last bot response but keep all previous messages
                const newConversation = conversation.slice(0, -1);
                
                logConversationChange('Removed last bot response for regeneration', {
                    originalLength: conversation.length,
                    newLength: newConversation.length,
                    lastUserMessage: lastUserMessage.msg.substring(0, 50) + '...'
                });
                
                setConversation(newConversation);
                
                // Use setTimeout to ensure state update is processed before sending
                setTimeout(() => {
                    sendMessage(lastUserMessage.msg, true); // Pass true for isRegeneration
                }, 50);
            }
        }
    };

    const handlePresetQuestion = (question) => {
        sendMessage(question);
    };

    return (
        <div className="chat-container">
            {showHistory && (
                <ChatHistory
                    ref={chatHistoryRef}
                    onSelectConversation={handleSelectConversation}
                    currentThreadId={thread_id}
                    onClose={() => setShowHistory(false)}
                />
            )}
            <div className="chat-header">
                <div className="header-left">
                    <img className="logo" src='main-logo.png' alt="PSEL Logo" />
                </div>
                <div className="header-actions">
                    <button
                        className="history-button"
                        onClick={() => setShowHistory(!showHistory)}
                        title="Chat History"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                        </svg>
                    </button>
                    <button
                        className="search-button"
                        onClick={() => setShowSearch(true)}
                        title="Search"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/>
                            <path d="m21 21-4.35-4.35"/>
                        </svg>
                    </button>
                    <button
                        className="settings-button"
                        onClick={() => setShowSettings(true)}
                        title="Settings"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                            <circle cx="12" cy="12" r="3"/>
                        </svg>
                    </button>
                    <button type="reset" onClick={clearChat}>New Chat</button>
                </div>
            </div>
            <div className="chat-body">
                {conversation.map((entry, index) => (
                    <div key={index} className={`chat-message ${entry.from} ${entry.isThinking ? 'thinking' : ''}`}>
                        {entry.isThinking ? (
                            <div className="message">
                                <TypingIndicator />
                            </div>
                        ) : (
                            <ChatMessage
                                message={entry.msg}
                                from={entry.from}
                                messageIndex={index}
                                timestamp={entry.timestamp}
                                onRegenerate={entry.from === 'bot' && index === conversation.length - 1 && !isMessageInProgress && !isThinking && chatEnabled ? handleRegenerateResponse : null}
                            />
                        )}
                    </div>
                ))}
                <div ref={endOfMessagesRef} style={{ height: '1px', minHeight: '1px' }} />
            </div>
            {conversation.length === 0 && (
                <div className="px-4">
                    <p className="text-gray-700 mb-3">Select a question or type your own message:</p>
                    <PresetQuestions onSelectQuestion={handlePresetQuestion} />
                </div>
            )}
            <p className='disclaimer'><em>Note: PSEL AI is experimental and can give unverified information</em></p>
            <form onSubmit={handleFormSubmit} className="chat-form">
                <div className='chat-input'>
                    <textarea
                        disabled={!chatEnabled}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type a message..."
                        onKeyDown={handleKeyDown}
                    />
                    <button disabled={!chatEnabled} type="submit">Send</button>
                </div>
            </form>
            
            {showSearch && (
                <SearchBar
                    conversation={conversation}
                    onSearchResults={setSearchResults}
                    isVisible={showSearch}
                    onClose={() => setShowSearch(false)}
                />
            )}
            
            {showSettings && (
                <SettingsPanel
                    isOpen={showSettings}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
};

export default Chatbox;